import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Einzelne Passwort-Regel.
 */
export interface PasswordRule {
  key: string;
  label: string;
  test: (pw: string) => boolean;
}

/**
 * Standard-Regeln, abgeleitet aus der Cognito User Pool Config (CDK auth-stack.ts).
 * Aenderungen an der Cognito-Policy muessen hier gespiegelt werden.
 * Kann per Props ueberschrieben werden, wenn andere Regeln gelten.
 */
export const DEFAULT_PASSWORD_RULES: PasswordRule[] = [
  { key: 'minLength', label: 'Mindestens 12 Zeichen', test: (pw: string) => pw.length >= 12 },
  { key: 'uppercase', label: 'Mindestens ein Grossbuchstabe', test: (pw: string) => /[A-Z]/.test(pw) },
  { key: 'lowercase', label: 'Mindestens ein Kleinbuchstabe', test: (pw: string) => /[a-z]/.test(pw) },
  { key: 'digit', label: 'Mindestens eine Ziffer', test: (pw: string) => /\d/.test(pw) },
  { key: 'special', label: 'Mindestens ein Sonderzeichen', test: (pw: string) => /[^A-Za-z0-9]/.test(pw) },
];

interface PasswordRequirementsProps {
  password: string;
  /** Optionale Regeln — Standard: DEFAULT_PASSWORD_RULES (aus Cognito Pool Config) */
  rules?: PasswordRule[];
  className?: string;
}

/**
 * Echtzeit-Passwort-Validierungs-Checkliste.
 * Zeigt fuer jede Regel ein Haekchen (erfuellt) oder Kreuz (nicht erfuellt).
 *
 * @see Spec 0043 Section 4.2/4.3 — Passwort-Anforderungen
 */
export function PasswordRequirements({ password, rules = DEFAULT_PASSWORD_RULES, className }: PasswordRequirementsProps) {
  return (
    <ul className={cn('space-y-1 text-sm', className)}>
      {rules.map((rule) => {
        const passed = password.length > 0 && rule.test(password);
        return (
          <li
            key={rule.key}
            className={cn(
              'flex items-center gap-2',
              passed ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
            )}
          >
            {passed ? (
              <Check className="h-3.5 w-3.5 flex-shrink-0" />
            ) : (
              <X className="h-3.5 w-3.5 flex-shrink-0" />
            )}
            <span>{rule.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Prueft ob ein Passwort alle Anforderungen erfuellt.
 * Kann sowohl in Komponenten als auch in Validierungs-Logik verwendet werden.
 */
export function isPasswordValid(password: string, rules: PasswordRule[] = DEFAULT_PASSWORD_RULES): boolean {
  return rules.every((rule) => rule.test(password));
}
